import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  console.warn('⚠️ Supabase env vars not set. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in .env.local');
}

// COMMERCIAL-DEMO: never use the service-role key here. REACT_APP_* vars are
// compiled into the shipped JS bundle, and the service key bypasses RLS on
// every table regardless of policy config — it must never leave a trusted
// server. Anon key only; RLS on `drafts` is what actually enforces access.
// Disable realtime WebSocket to avoid hitting connection quota on free tier
const supabase = createClient(SUPABASE_URL || '', SUPABASE_ANON_KEY || '', {
  realtime: { timeout: 0 },
});
supabase.realtime.disconnect();

// COMMERCIAL-DEMO: hard kill switch for all user-draft cloud writes.
// Demo users' draft preparation must stay strictly on their own machine —
// no Supabase sync, no Firestore sync (see FirebaseService.js counterpart).
const CLOUD_SAVE_DISABLED = true;

// camelCase → snake_case mapping for the drafts table
function toRow(draftData) {
  return {
    id: draftData.id || undefined,
    user_id: draftData.userId || '',
    user_email: draftData.userEmail || '',
    title: draftData.title || '',
    team_name: draftData.teamName || '',
    tournament: draftData.tournament || '',
    stage: draftData.stage || '',
    date: draftData.date || '',
    layout_version: draftData.layoutVersion || null,
    is_public: draftData.isPublic || false,
    scenarios_first_pick: draftData.scenariosFirstPick || [],
    scenarios_second_pick: draftData.scenariosSecondPick || [],
    forehead_picks: Array.isArray(draftData.foreheadPicks) ? draftData.foreheadPicks : [],
    forehead_connectors: Array.isArray(draftData.foreheadConnectors) ? draftData.foreheadConnectors : [],
    radiant_heroes: draftData.radiantHeroes || [],
    dire_heroes: draftData.direHeroes || [],
    radiant_drafts: draftData.radiantDrafts || {},
    dire_drafts: draftData.direDrafts || {},
    radiant_picks: draftData.radiantPicks || [],
    dire_picks: draftData.direPicks || [],
    radiant_bans: draftData.radiantBans || [],
    dire_bans: draftData.direBans || [],
    pick_timings: Array.isArray(draftData.pickTimings) ? draftData.pickTimings : [],
    gsi_match_id: draftData.gsiMatchId || null,
    created_at: draftData.createdAt || new Date().toISOString(),
    updated_at: draftData.updatedAt || new Date().toISOString(),
  };
}

// snake_case row → camelCase draft object (full data)
function fromRow(row) {
  return {
    id: row.id,
    title: row.title || '',
    teamName: row.team_name || '',
    tournament: row.tournament || '',
    stage: row.stage || '',
    date: row.date || '',
    layoutVersion: row.layout_version || null,
    isPublic: row.is_public || false,
    userId: row.user_id || '',
    userEmail: row.user_email || '',
    scenariosFirstPick: row.scenarios_first_pick || [],
    scenariosSecondPick: row.scenarios_second_pick || [],
    foreheadPicks: Array.isArray(row.forehead_picks) && row.forehead_picks.length >= 15 ? row.forehead_picks : Array(15).fill(null),
    foreheadConnectors: Array.isArray(row.forehead_connectors) ? row.forehead_connectors : [],
    radiantHeroes: row.radiant_heroes || [],
    direHeroes: row.dire_heroes || [],
    radiantDrafts: row.radiant_drafts || {},
    direDrafts: row.dire_drafts || {},
    radiantPicks: row.radiant_picks || [],
    direPicks: row.dire_picks || [],
    radiantBans: row.radiant_bans || [],
    direBans: row.dire_bans || [],
    pickTimings: Array.isArray(row.pick_timings) ? row.pick_timings : [],
    gsiMatchId: row.gsi_match_id || null,
    updatedAt: row.updated_at || new Date().toISOString(),
    createdAt: row.created_at || new Date().toISOString(),
  };
}

// Lightweight metadata (no scenario JSONB) for RecordViewer list queries
function fromRowMeta(row) {
  return {
    id: row.id,
    title: row.title || '',
    teamName: row.team_name || '',
    tournament: row.tournament || '',
    stage: row.stage || '',
    date: row.date || '',
    layoutVersion: row.layout_version || null,
    isPublic: row.is_public || false,
    userId: row.user_id || '',
    userEmail: row.user_email || '',
    updatedAt: row.updated_at || new Date().toISOString(),
    createdAt: row.created_at || new Date().toISOString(),
    // Scenario counts from separate fields if available
    scenariosFirstPick: [],
    scenariosSecondPick: [],
  };
}

class SupabaseService {
  /**
   * Save (upsert) a draft to Supabase.
   * Returns the draft id.
   */
  async saveDraft(draftData) {
    // COMMERCIAL-DEMO: cloud saving disabled — drafts never leave this machine.
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Supabase saveDraft skipped (commercial-demo: local-only build)');
      return draftData.id || ('local_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8));
    }
    if (!draftData.userId) {
      throw new Error('userId is required to save a draft');
    }

    const now = new Date().toISOString();
    draftData.updatedAt = now;
    if (!draftData.createdAt) {
      draftData.createdAt = now;
    }

    const row = toRow(draftData);

    // If no id, remove it so Postgres generates a UUID or we use addDoc pattern
    // For Supabase we use a text PK - generate one if missing
    if (!row.id) {
      row.id = 'draft_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
    }

    // Try INSERT first; if duplicate key, fall back to UPDATE
    const { error: insertError } = await supabase
      .from('drafts')
      .insert(row);

    if (insertError) {
      if (insertError.code === '23505') {
        // Row exists — update it instead
        const { error: updateError } = await supabase
          .from('drafts')
          .update(row)
          .eq('id', row.id);
        if (updateError) {
          console.error('❌ Supabase saveDraft update error:', updateError);
          throw new Error(`Supabase update failed (${updateError.code}): ${updateError.message}`);
        }
      } else {
        console.error('❌ Supabase saveDraft insert error:', insertError);
        console.error('❌ Code:', insertError.code, 'Message:', insertError.message, 'Details:', insertError.details);
        throw new Error(`Supabase insert failed (${insertError.code}): ${insertError.message}`);
      }
    }

    return row.id;
  }

  /**
   * Fetch a draft by Dota 2 GSI match ID.
   * Returns null if not found.
   */
  async getDraftByGsiMatchId(gsiMatchId) {
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Supabase ' + 'getDraftByGsiMatchId' + ' skipped (commercial-demo: local-only build)');
      return null;
    }
    const { data, error } = await supabase
      .from('drafts')
      .select('*')
      .eq('gsi_match_id', String(gsiMatchId))
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('❌ Supabase getDraftByGsiMatchId error:', error);
      throw error;
    }

    return data ? fromRow(data) : null;
  }

  /**
   * Return aggregated pick timings across all drafts for a user.
   * Groups by pickMode (first/second) and boardPos, computes avg/min/max deltaSeconds.
   * Delta = time between consecutive picks in detection order.
   */
  async getPickTimingsAggregated(userId) {
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Supabase getPickTimingsAggregated skipped (commercial-demo: local-only build)');
      return { first: {}, second: {}, totalDrafts: 0 };
    }
    const { data, error } = await supabase
      .from('drafts')
      .select('gsi_match_id, pick_timings, scenarios_first_pick')
      .eq('user_id', userId)
      .not('pick_timings', 'eq', '[]')
      .not('gsi_match_id', 'is', null);

    if (error) {
      console.error('❌ Supabase getPickTimingsAggregated error:', error);
      throw error;
    }

    // Compute deltas from detectedAt timestamps and aggregate
    const byMode = { first: {}, second: {} };

    for (const row of (data || [])) {
      const timings = Array.isArray(row.pick_timings) ? row.pick_timings : [];
      if (timings.length === 0) continue;

      // Sort by seq to get chronological order
      const sorted = [...timings].sort((a, b) => a.seq - b.seq);

      // Determine pick mode from swapSides on the live scenario:
      // swapSides=false → left=Radiant positions (1,2,5,8) → YG is 1st pick
      // swapSides=true  → left=Dire positions (3,4,6,7) → YG is 2nd pick
      const fp = Array.isArray(row.scenarios_first_pick) ? row.scenarios_first_pick : [];
      const liveScenario = fp.find(s => s.gsiMatchId === row.gsi_match_id);
      const mode = liveScenario?.swapSides ? 'second' : 'first';

      for (let i = 0; i < sorted.length; i++) {
        const entry = sorted[i];
        // First pick uses baselineAt (match detection time) as the previous timestamp
        const prevTs = i === 0
          ? (entry.baselineAt || null)
          : sorted[i - 1].detectedAt;
        if (!prevTs) continue;
        const delta = Math.round((entry.detectedAt - prevTs) / 1000);
        if (!Number.isFinite(delta) || delta < 0 || delta > 600) continue; // sanity

        const key = entry.boardPos;
        if (!byMode[mode][key]) byMode[mode][key] = [];
        byMode[mode][key].push(delta);
      }
    }

    // Summarize
    const summarize = (buckets) => {
      const result = {};
      for (const [pos, deltas] of Object.entries(buckets)) {
        const avg = deltas.reduce((s, v) => s + v, 0) / deltas.length;
        result[pos] = { avg: Math.round(avg), min: Math.min(...deltas), max: Math.max(...deltas), count: deltas.length };
      }
      return result;
    };

    return {
      first: summarize(byMode.first),
      second: summarize(byMode.second),
      totalDrafts: (data || []).length,
    };
  }

  /**
   * Fetch a single draft with full scenario data.
   */
  async getDraft(draftId) {
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Supabase ' + 'getDraft' + ' skipped (commercial-demo: local-only build)');
      return null;
    }
    const { data, error } = await supabase
      .from('drafts')
      .select('*')
      .eq('id', draftId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // not found
      console.error('❌ Supabase getDraft error:', error);
      throw error;
    }

    return fromRow(data);
  }

  /**
   * Fetch all drafts for a user (full data including scenarios).
   * Used by DraftContext for loading draft lists.
   */
  async getUserDrafts(userId) {
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Supabase ' + 'getUserDrafts' + ' skipped (commercial-demo: local-only build)');
      return [];
    }
    const { data, error } = await supabase
      .from('drafts')
      .select('*')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('❌ Supabase getUserDrafts error:', error);
      throw error;
    }

    return (data || []).map(fromRow);
  }

  /**
   * Delete a draft by id (with user_id safety check).
   */
  async deleteDraft(draftId, userId) {
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Supabase deleteDraft skipped (commercial-demo: local-only build)');
      return true;
    }
    let query = supabase.from('drafts').delete().eq('id', draftId);
    if (userId) {
      query = query.eq('user_id', userId);
    }
    const { error } = await query;
    if (error) {
      console.error('❌ Supabase deleteDraft error:', error);
      throw new Error(`Supabase delete failed (${error.code}): ${error.message}`);
    }
    return true;
  }

  /**
   * Fetch public drafts (excluding the current user's own drafts).
   */
  async getPublicDrafts(excludeUserId) {
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Supabase ' + 'getPublicDrafts' + ' skipped (commercial-demo: local-only build)');
      return [];
    }
    let query = supabase
      .from('drafts')
      .select('*')
      .eq('is_public', true)
      .order('updated_at', { ascending: false });

    if (excludeUserId) {
      query = query.neq('user_id', excludeUserId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('❌ Supabase getPublicDrafts error:', error);
      throw error;
    }

    return (data || []).map(fromRow);
  }

  /**
   * Lightweight paginated query for RecordViewer.
   * Returns only metadata columns — no scenario JSONB blobs.
   *
   * @param {string} userId
   * @param {{ teamSearch?: string, tournamentSearch?: string, page?: number, pageSize?: number }} opts
   * @returns {{ drafts: DraftMeta[], totalCount: number }}
   */
  async getRecordViewerPage(userId, opts = {}) {
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Supabase ' + 'getRecordViewerPage' + ' skipped (commercial-demo: local-only build)');
      return { drafts: [], totalCount: 0 };
    }
    const {
      teamSearch = '',
      tournamentSearch = '',
      page = 1,
      pageSize = 50,
    } = opts;

    const {
      exactTeamName = null,
    } = opts;

    const selectCols = 'id, user_id, user_email, title, team_name, tournament, stage, date, layout_version, is_public, created_at, updated_at, scenarios_first_pick, scenarios_second_pick';

    let query = supabase
      .from('drafts')
      .select(selectCols, { count: 'exact' })
      .eq('user_id', userId);

    if (exactTeamName) {
      query = query.eq('team_name', exactTeamName);
    } else if (teamSearch.trim()) {
      query = query.ilike('team_name', `%${teamSearch.trim()}%`);
    }

    if (tournamentSearch.trim()) {
      query = query.ilike('tournament', `%${tournamentSearch.trim()}%`);
    }

    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    query = query.order('updated_at', { ascending: false }).range(from, to);

    const { data, error, count } = await query;

    if (error) {
      console.error('❌ Supabase getRecordViewerPage error:', error);
      throw error;
    }

    return {
      drafts: (data || []).map(fromRow),
      totalCount: count || 0,
    };
  }

  /**
   * Fetch only the scenario JSONB for a single draft (lazy load on card expand).
   */
  async getDraftScenarios(draftId) {
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Supabase ' + 'getDraftScenarios' + ' skipped (commercial-demo: local-only build)');
      return null;
    }
    const { data, error } = await supabase
      .from('drafts')
      .select('id, scenarios_first_pick, scenarios_second_pick, layout_version')
      .eq('id', draftId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      console.error('❌ Supabase getDraftScenarios error:', error);
      throw error;
    }

    return {
      id: data.id,
      scenariosFirstPick: data.scenarios_first_pick || [],
      scenariosSecondPick: data.scenarios_second_pick || [],
      layoutVersion: data.layout_version || null,
    };
  }

  /**
   * Migrate drafts one-at-a-time to avoid large payload issues.
   * Each draft is upserted individually so scenario JSONB never batches up.
   *
   * @param {object[]} drafts - Array of draft objects in camelCase format
   * @param {function} onProgress - Callback(done, total)
   */
  async migrateBatch(drafts, onProgress) {
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Supabase migrateBatch skipped (commercial-demo: local-only build)');
      return 0;
    }
    let done = 0;

    for (const d of drafts) {
      const row = toRow(d);
      if (!row.id) {
        row.id = 'draft_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
      }

      // Plain INSERT via Supabase JS client — no Prefer header, no upsert
      // (Supabase is empty on first migration so no duplicate conflicts expected)
      const { error } = await supabase
        .from('drafts')
        .insert(row);

      if (error) {
        // If duplicate, skip it (already migrated)
        if (error.code === '23505') {
          console.warn('⚠️ Draft already in Supabase, skipping:', row.id);
        } else {
          console.error('❌ migrateBatch insert error for draft', row.id, error);
          console.error('❌ Code:', error.code, 'Message:', error.message, 'Details:', error.details);
          throw new Error(`Supabase insert error (${error.code || 'unknown'}): ${error.message}`);
        }
      }

      done += 1;
      if (onProgress) onProgress(done, drafts.length);
    }

    return done;
  }

  /**
   * Diagnose Supabase connectivity — call from browser console:
   *   import supabaseService from './services/SupabaseService'; supabaseService.testWrite();
   */
  async testWrite() {
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Supabase testWrite skipped (commercial-demo: local-only build)');
      return null;
    }
    const testId = 'test_' + Date.now();
    const url = `${SUPABASE_URL}/rest/v1/drafts`;
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=minimal',
        },
        body: JSON.stringify({ id: testId, user_id: 'test', title: 'connectivity-test' }),
      });
      const text = await res.text().catch(() => '');
      console.log('testWrite status:', res.status, text);
      if (res.ok) {
        // clean up
        await fetch(`${url}?id=eq.${testId}`, {
          method: 'DELETE',
          headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` },
        });
        console.log('✅ Supabase write test PASSED');
      }
      return res.status;
    } catch (e) {
      console.error('❌ Supabase write test FAILED:', e);
      return null;
    }
  }

  /**
   * Get distinct team names for a user, sorted by most recent draft.
   * Returns [{teamName, latestUpdatedAt}]
   */
  async getTeamList(userId) {
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Supabase ' + 'getTeamList' + ' skipped (commercial-demo: local-only build)');
      return [];
    }
    const { data, error } = await supabase
      .from('drafts')
      .select('team_name, updated_at')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('❌ Supabase getTeamList error:', error);
      return [];
    }

    // Deduplicate by team_name, keeping most recent (first occurrence since sorted desc)
    const seen = new Set();
    const teams = [];
    for (const row of (data || [])) {
      const name = row.team_name || '';
      if (!seen.has(name)) {
        seen.add(name);
        teams.push({ teamName: name, latestUpdatedAt: row.updated_at });
      }
    }
    return teams;
  }

  // ───────────────────────────────────────────────────────────────────────
  // Pro-match draft data (the ygbuff "Draft Layout" tab → shared `pro_matches`
  // table). Used by the Scout Simulator to predict a real team's draft moves
  // from historical pro drafts. Read-only; lives in the same Supabase project.
  // ───────────────────────────────────────────────────────────────────────

  /** Columns the Scout Simulator needs from pro_matches. */
  static get PRO_MATCH_COLUMNS() {
    return (
      'match_id,date,tournament_name,radiant_name,dire_name,first_pick,gamer_winner,' +
      'pos_1st,pos_2nd,pos_3rd,pos_4th,pos_5th,pos_6th,pos_7th,pos_8th,pos_9th,pos_10th,' +
      'pos_11th,pos_12th,pos_13th,pos_14th,pos_15th,pos_16th,pos_17th,pos_18th,pos_19th,' +
      'pos_20th,pos_21st,pos_22nd,pos_23rd,pos_24th'
    );
  }

  /**
   * Distinct team names in pro_matches with their match counts, sorted desc.
   * Primary path: the `get_pro_teams()` Postgres RPC (see supabase-schema.md).
   * Fallback: paginate radiant/dire columns (REST caps at 1000 rows) and dedupe.
   * Cached in-memory for the session.
   */
  async getProTeams() {
    if (this._proTeamsCache) return this._proTeamsCache;

    const { data, error } = await supabase.rpc('get_pro_teams');
    if (!error && Array.isArray(data) && data.length > 0) {
      const teams = data
        .filter(r => r.team)
        .map(r => ({ team: r.team, matches: Number(r.matches) || 0 }));
      this._proTeamsCache = teams;
      return teams;
    }
    if (error) {
      console.warn('⚠️ get_pro_teams RPC unavailable, falling back to pagination:', error.message);
    }

    // Fallback: page through radiant_name/dire_name and tally client-side.
    const counts = new Map();
    const PAGE = 1000;
    for (let from = 0; ; from += PAGE) {
      const { data: rows, error: perrErr } = await supabase
        .from('pro_matches')
        .select('radiant_name,dire_name')
        .range(from, from + PAGE - 1);
      if (perrErr) {
        console.error('❌ Supabase getProTeams fallback error:', perrErr);
        break;
      }
      (rows || []).forEach(r => {
        [r.radiant_name, r.dire_name].forEach(name => {
          if (name) counts.set(name, (counts.get(name) || 0) + 1);
        });
      });
      if (!rows || rows.length < PAGE) break;
    }
    const teams = Array.from(counts.entries())
      .map(([team, matches]) => ({ team, matches }))
      .sort((a, b) => b.matches - a.matches);
    this._proTeamsCache = teams;
    return teams;
  }

  /**
   * All pro_matches a team played (radiant or dire), optional date window.
   * @param {string} teamName exact team name from getProTeams()
   * @param {{fromDate?:string, toDate?:string}} opts ISO YYYY-MM-DD bounds
   * @returns {Promise<Array>} raw pro_matches rows (snake_case)
   */
  async getTeamProMatches(teamName, opts = {}) {
    if (!teamName) return [];
    const { fromDate, toDate } = opts;
    // PostgREST .or() with ilike — escape commas in team names by matching exact-ish.
    const safe = String(teamName).replace(/[,()]/g, ' ').trim();
    let query = supabase
      .from('pro_matches')
      .select(SupabaseService.PRO_MATCH_COLUMNS)
      .or(`radiant_name.ilike.${safe},dire_name.ilike.${safe}`)
      .order('date', { ascending: false });
    if (fromDate) query = query.gte('date', fromDate);
    if (toDate) query = query.lte('date', toDate);

    const { data, error } = await query;
    if (error) {
      console.error('❌ Supabase getTeamProMatches error:', error);
      return [];
    }
    return data || [];
  }

  /**
   * Check if there are any drafts for this user (used to show migration button).
   */
  async getDraftCount(userId) {
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Supabase ' + 'getDraftCount' + ' skipped (commercial-demo: local-only build)');
      return 0;
    }
    const { count, error } = await supabase
      .from('drafts')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId);

    if (error) {
      console.error('❌ Supabase getDraftCount error:', error);
      return 0;
    }

    return count || 0;
  }
}

const supabaseService = new SupabaseService();
export default supabaseService;
